import { useState, useEffect } from "react";
import { Avatar, Table, Tag, Row, Col, Statistic, Button, Modal } from "antd";
import { Users, Download, FileText, TableProperties } from "lucide-react";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { getEventAttendees } from "services/portalEvents.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import { BOOKING_STATUS_COLOR } from "commons/constants";

const columns = [
  {
    title: "Customer",
    key: "customer",
    render: (_, r) => (
      <Row gutter={[10, 0]} align="middle" wrap={false}>
        <Col>
          <Avatar src={r.customer_details?.profile_image_url} size={36}>
            {r.customer_details?.full_name?.[0]}
          </Avatar>
        </Col>
        <Col>
          <p className="font-medium text-sm leading-tight">
            {r.customer_details?.full_name}
          </p>
          <p className="text-xs text-muted-foreground">
            {r.customer_details?.phone_number}
          </p>
        </Col>
      </Row>
    ),
  },
  {
    title: "Booking #",
    dataIndex: "booking_number",
    key: "booking_number",
    render: (v) => <span className="font-mono text-xs">{v}</span>,
  },
  {
    title: "Ticket",
    key: "ticket",
    render: (_, r) => (
      <span className="text-sm text-muted-foreground">
        {r.product_details?.name}
      </span>
    ),
  },
  {
    title: "Qty",
    dataIndex: "quantity",
    key: "quantity",
    align: "center",
    width: 60,
  },
  {
    title: "Status",
    dataIndex: "booking_status",
    key: "booking_status",
    render: (v) => <Tag color={BOOKING_STATUS_COLOR[v]}>{v}</Tag>,
  },
  {
    title: "Confirmed At",
    dataIndex: "booking_confirmed_at",
    key: "booking_confirmed_at",
    render: (v) =>
      v && (
        <span className="text-xs text-muted-foreground">
          {dayjs(v).format("DD-MMM-YYYY • h:mm A")}
        </span>
      ),
  },
];

function buildPdf(rows, event) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  const title = event?.title ?? "Event";
  const dateRange = [event?.start_date_time, event?.end_date_time]
    .filter(Boolean)
    .map((d) => dayjs(d).format("DD-MMM-YYYY"))
    .join(" – ");
  const venue = event?.location?.name ?? "";
  const totalQty = rows.reduce((sum, r) => sum + (r.quantity ?? 0), 0);

  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 40;

  // ── Dark header band ──────────────────────────────────────────────────────
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, PAGE_W, 72, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(`Bookings List — ${title}`, PAGE_W / 2, 28, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const subtitle = [dateRange, venue, "All Confirmed Bookings"]
    .filter(Boolean)
    .join("   |   ");
  doc.text(subtitle, PAGE_W / 2, 48, { align: "center" });

  // ── Table ─────────────────────────────────────────────────────────────────
  const tableRows = rows.map((r, i) => [
    i + 1,
    r.booking_number,
    r.customer_details?.full_name,
    r.customer_details?.phone_number,
    r.product_details?.name,
    r.quantity,
    r.booking_confirmed_at
      ? dayjs(r.booking_confirmed_at).format("DD-MMM-YYYY • h:mm A")
      : "",
  ]);

  autoTable(doc, {
    startY: 88,
    margin: { left: MARGIN, right: MARGIN },
    head: [
      [
        "Sr.",
        "Booking Number",
        "Customer Name",
        "Phone Number",
        "Ticket Type",
        "Qty",
        "Confirmed At",
      ],
    ],
    body: tableRows,
    foot: [["", `TOTAL — ${rows.length} Bookings`, "", "", "", totalQty, ""]],
    theme: "grid",
    showFoot: "lastPage",
    styles: { fontSize: 8.5, cellPadding: 5, overflow: "linebreak" },
    headStyles: {
      fillColor: [30, 41, 59], // slate-800
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "left",
    },
    footStyles: {
      fillColor: [241, 245, 249], // slate-100
      textColor: [15, 23, 42],
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [248, 250, 252] }, // slate-50
    columnStyles: {
      0: { cellWidth: 24, halign: "center" },
      5: { cellWidth: 30, halign: "center" },
    },
  });

  const safeTitle = title
    .replace(/[^a-zA-Z0-9_\- ]/g, "")
    .trim()
    .replace(/\s+/g, "_");

  doc.save(`${safeTitle}_bookings_list.pdf`);
}

function buildExcel(rows, event) {
  const title = event?.title ?? "Event";
  const totalQty = rows.reduce((sum, r) => sum + (r.quantity ?? 0), 0);

  const dataRows = rows.map((r, i) => [
    i + 1,
    r.booking_number,
    r.customer_details?.full_name,
    r.customer_details?.phone_number,
    r.product_details?.name,
    r.quantity,
    r.booking_confirmed_at
      ? dayjs(r.booking_confirmed_at).format("DD-MMM-YYYY • h:mm A")
      : "",
  ]);

  const ws = XLSX.utils.aoa_to_sheet([
    ["Sr.", "Booking Number", "Customer Name", "Phone Number", "Ticket Type", "Qty", "Confirmed At"],
    ...dataRows,
    ["", `TOTAL — ${rows.length} Bookings`, "", "", "", totalQty, ""],
  ]);

  ws["!cols"] = [
    { wch: 5 }, { wch: 16 }, { wch: 22 }, { wch: 14 },
    { wch: 28 }, { wch: 5 }, { wch: 24 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bookings");

  const safeTitle = title.replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_");
  XLSX.writeFile(wb, `${safeTitle}_bookings_list.xlsx`);
}

function AttendeesTab({ eventId, isPublished, event }) {
  const [attendees, setAttendees] = useState([]);
  const [totalEventAttendees, setTotalEventAttendees] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportRows, setExportRows] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  async function loadAttendees(page, pageSize) {
    if (!isPublished || !eventId) return;
    setLoading(true);
    const { data, errors } = await getEventAttendees({
      eventIds: [eventId],
      current_page: page,
      page_size: pageSize,
    });
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setAttendees(data.attendees_list?.data ?? []);
    setTotalEventAttendees(data.total_event_attendees ?? 0);
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
      total: data.attendees_list?.meta?.pagination?.total ?? 0,
    }));
  }

  useEffect(() => {
    loadAttendees(1, 10);
  }, [eventId, isPublished]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (paginationConfig) => {
    loadAttendees(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleExport = async () => {
    setExporting(true);
    const { data, errors } = await getEventAttendees({
      eventIds: [eventId],
      current_page: 1,
      page_size: 2000,
    });
    setExporting(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setExportRows(data.attendees_list?.data ?? []);
  };

  const handleFormatPick = (fmt) => {
    if (fmt === "pdf") buildPdf(exportRows, event);
    else buildExcel(exportRows, event);
    setExportRows(null);
  };

  if (!isPublished) {
    return (
      <div className="rounded-2xl border bg-card shadow-sm">
        <div className="p-12 text-center text-muted-foreground space-y-4">
          <Users className="h-12 w-12 mx-auto opacity-20" />
          <p>Attendee management will be available once the event goes live.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Row gutter={16} align="middle" justify="space-between">
        <Col>
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <Statistic
              title="Total Attendees"
              value={totalEventAttendees}
              prefix={<Users className="h-4 w-4 inline mr-1" />}
            />
          </div>
        </Col>
        <Col>
          <Button
            icon={<Download size={15} />}
            loading={exporting}
            onClick={handleExport}
            disabled={totalEventAttendees === 0}
          >
            Export
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="booking_number"
        columns={columns}
        dataSource={attendees}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (t) => `Total ${t} bookings`,
          showSizeChanger: false,
        }}
        scroll={{ x: 700 }}
      />

      <Modal
        open={!!exportRows}
        onCancel={() => setExportRows(null)}
        title="Choose export format"
        footer={null}
        width={320}
        centered
      >
        <div style={{ display: "flex", gap: 12, padding: "16px 0" }}>
          <Button
            block
            size="large"
            icon={<FileText size={16} />}
            onClick={() => handleFormatPick("pdf")}
          >
            PDF
          </Button>
          <Button
            block
            size="large"
            icon={<TableProperties size={16} />}
            onClick={() => handleFormatPick("excel")}
          >
            Excel
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AttendeesTab;
